
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from '@/i18n';
import { User } from '../types';
import { supabase } from '@/integrations/supabase/client';

interface OwnedApartment {
  id: string;
  apartment_number: string;
  building_id: string;
  building_name?: string;
  is_primary_owner: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  userRole: 'admin' | 'owner' | 'concierge' | 'user' | null;
  isPlatformAdmin: boolean;
  isSuperAdmin: boolean; // Alias for isPlatformAdmin
  isOwnerUser: boolean;
  isConciergeUser: boolean;
  ownedApartments: OwnedApartment[];
  conciergeApartments: OwnedApartment[];
  effectiveOrganizationId: string | null; // For super admins: selected org, for others: their org
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (userData: Partial<User> & { password: string }) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isValidatingOrg: boolean;
  updateUser: (userData: Partial<User>) => Promise<void>;
  isAdmin: () => boolean;
  isPlatformAdminUser: () => boolean;
  isSuperAdminUser: () => boolean; // Alias for isPlatformAdminUser
  isOwner: () => boolean;
  isConcierge: () => boolean;
  getEffectiveOrganizationId: () => Promise<string | null>; // Get org ID to use for queries
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'owner' | 'concierge' | 'user' | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);
  const [isOwnerUser, setIsOwnerUser] = useState<boolean>(false);
  const [isConciergeUser, setIsConciergeUser] = useState<boolean>(false);
  const [ownedApartments, setOwnedApartments] = useState<OwnedApartment[]>([]);
  const [conciergeApartments, setConciergeApartments] = useState<OwnedApartment[]>([]);
  const [effectiveOrganizationId, setEffectiveOrganizationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Flag to prevent auto-redirect during organization validation
  const [isValidatingOrg, setIsValidatingOrg] = useState(false);

  // Get the effective organization ID for queries
  // Super admins use the selected org from localStorage, others use their own org
  const getEffectiveOrganizationId = async (): Promise<string | null> => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return null;

    // Platform-admin flag now lives in app_metadata (service-role-only; the
    // old user_metadata source was self-writable and has been removed as the
    // source of truth).
    const isSuperAdmin = authUser.app_metadata?.is_platform_admin === true;

    if (isSuperAdmin) {
      // Super admin: use the organization they selected at login
      const selectedOrgId = localStorage.getItem('organizationId');
      if (selectedOrgId) {
        return selectedOrgId;
      }
    }

    // Regular user: use their own organization_id
    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', authUser.id)
      .single();

    return userData?.organization_id || null;
  };

  useEffect(() => {
    // Set up Supabase auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Redirect to reset-password page on PASSWORD_RECOVERY event
      // This handles the case where Supabase redirects to the root URL
      // instead of /reset-password (e.g., when redirect URL is not whitelisted)
      if (event === 'PASSWORD_RECOVERY' && !window.location.pathname.includes('/reset-password')) {
        window.location.href = '/reset-password';
        return;
      }

      setToken(session?.access_token ?? null);

      if (session?.user) {
        const uid = session.user.id;
        // Defer Supabase calls to avoid deadlocks in the callback
        setTimeout(async () => {
          try {
            // Load user profile from our public.users table
            const { data: dbUser } = await supabase
              .from('users')
              .select('*')
              .eq('id', uid)
              .maybeSingle();

            // Deactivated account: reject here, in the SAME path that sets `user`,
            // not just in login()'s own separate status check. login() and this
            // listener both react to the same signInWithPassword sign-in
            // independently - if only login() rejected inactive accounts, its
            // signOut() could be overtaken by this listener's (slower, DB-query-laden)
            // setUser(mappedUser) call still landing afterward, leaving the app
            // believing a deactivated, already-signed-out account was logged in
            // (confirmed: a real deactivated account produced a valid-looking JWT
            // with no corresponding auth.sessions/refresh_tokens row - login() had
            // already signed it out, but this listener set `user` anyway).
            if (dbUser?.status === 'inactive') {
              console.warn('[Auth] Blocking session for deactivated account', uid);
              await supabase.auth.signOut();
              setUser(null);
              setUserRole(null);
              setIsPlatformAdmin(false);
              setIsOwnerUser(false);
              setIsConciergeUser(false);
              setOwnedApartments([]);
              setConciergeApartments([]);
              setIsLoading(false);
              return;
            }

            let mappedUser: User;
            if (dbUser) {
              mappedUser = {
                id: dbUser.id,
                email: dbUser.email,
                firstName: dbUser.first_name || '',
                lastName: dbUser.last_name || '',
                clientNumber: dbUser.client_number || '',
                address: { street: '', city: '', zipCode: '' },
                preferences: { emailNotifications: true, pushNotifications: true, phoneNotifications: false },
              };
            } else {
              const u = session.user;
              mappedUser = {
                id: u.id,
                email: u.email || '',
                firstName: '',
                lastName: '',
                clientNumber: '',
                address: { street: '', city: '', zipCode: '' },
                preferences: { emailNotifications: true, pushNotifications: true, phoneNotifications: false },
              };
            }

            // Check if user is a platform admin from app_metadata (service-role-only).
            const userIsPlatformAdmin = session.user.app_metadata?.is_platform_admin === true;

            // Organization mismatch: same authoritative-check reasoning as the
            // deactivated-account block above - login()'s own rejection here could
            // otherwise be overtaken by this listener's setUser(mappedUser) below.
            // Platform admins are exempt (they can access any organization).
            const selectedOrgId = localStorage.getItem('organizationId');
            if (!userIsPlatformAdmin && selectedOrgId && dbUser?.organization_id && dbUser.organization_id !== selectedOrgId) {
              console.warn('[Auth] Blocking session: organization mismatch', uid);
              await supabase.auth.signOut();
              setUser(null);
              setUserRole(null);
              setIsPlatformAdmin(false);
              setIsOwnerUser(false);
              setIsConciergeUser(false);
              setOwnedApartments([]);
              setConciergeApartments([]);
              setIsLoading(false);
              return;
            }

            // Run role detection queries in parallel for speed
            const [adminResult, ownerResult, conciergeResult] = await Promise.all([
              // Determine role via organization_admins
              supabase
                .from('organization_admins')
                .select('id')
                .eq('user_id', uid)
                .limit(1)
                .maybeSingle(),
              // Check if user is an owner (has entries in unit_owners)
              supabase
                .from('unit_owners')
                .select(`
                  id,
                  apartment_id,
                  is_primary_owner,
                  apartments:apartment_id (
                    id,
                    apartment_number,
                    building_id,
                    buildings:building_id (
                      name
                    )
                  )
                `)
                .eq('user_id', uid),
              // Check if user is a concierge (has entries in concierge_assignments)
              supabase
                .from('concierge_assignments')
                .select(`
                  id,
                  apartment_id,
                  apartments:apartment_id (
                    id,
                    apartment_number,
                    building_id,
                    buildings:building_id (
                      name
                    )
                  )
                `)
                .eq('concierge_id', uid),
            ]);

            const adminRow = adminResult.data;
            const ownerData = ownerResult.data;
            const conciergeData = conciergeResult.data;

            const userIsOwner = ownerData && ownerData.length > 0;
            const userIsConcierge = conciergeData && conciergeData.length > 0;

            // Process owned apartments
            let resolvedOwnedApartments: OwnedApartment[] = [];
            if (userIsOwner) {
              resolvedOwnedApartments = ownerData.map((row: any) => ({
                id: row.apartments?.id || row.apartment_id,
                apartment_number: row.apartments?.apartment_number || '',
                building_id: row.apartments?.building_id || '',
                building_name: row.apartments?.buildings?.name || '',
                is_primary_owner: row.is_primary_owner
              }));
            }

            // Process concierge apartments
            let resolvedConciergeApartments: OwnedApartment[] = [];
            if (userIsConcierge) {
              resolvedConciergeApartments = conciergeData.map((row: any) => ({
                id: row.apartments?.id || row.apartment_id,
                apartment_number: row.apartments?.apartment_number || '',
                building_id: row.apartments?.building_id || '',
                building_name: row.apartments?.buildings?.name || '',
                is_primary_owner: false
              }));
            }

            // Determine role hierarchy: platform_admin > admin > owner > concierge > user
            let role: 'admin' | 'owner' | 'concierge' | 'user' = 'user';
            if (adminRow || userIsPlatformAdmin) {
              role = 'admin';
            } else if (userIsOwner) {
              role = 'owner';
            } else if (userIsConcierge) {
              role = 'concierge';
            }

            // CRITICAL: Batch all state updates together so React renders once
            // with correct user + role, preventing wrong redirect on first render
            setUser(mappedUser);
            setIsPlatformAdmin(userIsPlatformAdmin);
            setIsOwnerUser(userIsOwner);
            setOwnedApartments(resolvedOwnedApartments);
            setIsConciergeUser(userIsConcierge);
            setConciergeApartments(resolvedConciergeApartments);
            setUserRole(role);

            // Persist to localStorage
            localStorage.setItem('sme_user', JSON.stringify(mappedUser));
            localStorage.setItem('sme_user_role', role);
            localStorage.setItem('sme_is_platform_admin', String(userIsPlatformAdmin));
            localStorage.setItem('sme_is_owner', String(userIsOwner));
            localStorage.setItem('sme_is_concierge', String(userIsConcierge));
            localStorage.setItem('sme_token', session?.access_token ?? '');
            localStorage.setItem('sme_token_expiry', (Date.now() + 24 * 60 * 60 * 1000).toString());
            if (userIsOwner) {
              localStorage.setItem('sme_owned_apartments', JSON.stringify(resolvedOwnedApartments));
            } else {
              localStorage.removeItem('sme_owned_apartments');
            }
            if (userIsConcierge) {
              localStorage.setItem('sme_concierge_apartments', JSON.stringify(resolvedConciergeApartments));
            } else {
              localStorage.removeItem('sme_concierge_apartments');
            }
          } finally {
            setIsLoading(false);
          }
        }, 0);
      } else {
        setUser(null);
        setUserRole(null);
        setIsPlatformAdmin(false);
        setIsOwnerUser(false);
        setIsConciergeUser(false);
        setOwnedApartments([]);
        setConciergeApartments([]);
        setIsLoading(false);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setToken(session?.access_token ?? null);
      if (!session) {
        // Removed localStorage fallback for security - only use Supabase auth
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setIsValidatingOrg(true); // Prevent auto-redirect during validation
    try {
      // Primary: authenticate with Supabase so RLS policies allow admin data access
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.error('Auth error:', error.message);

        // Map Supabase errors to user-friendly, TRANSLATED messages (previously
        // hardcoded English regardless of the user's selected language - the
        // rest of this page is localized via t(), but every login error bypassed
        // it entirely).
        let errorMessage = i18n.t('auth.loginFailed');

        // Case-insensitive matching, plus status-code/offline checks: the two
        // previous lowercase-only checks ('too many requests', 'network') could
        // never actually match Supabase/browser's real error text ("Too many
        // requests" / "Failed to fetch" / "NetworkError when attempting to
        // fetch resource" all differ in case or wording), so both silently
        // always fell through to the generic message below.
        const msg = error.message.toLowerCase();
        const status = (error as { status?: number }).status;
        if (!navigator.onLine) {
          errorMessage = i18n.t('auth.offlineError');
        } else if (msg.includes('invalid login credentials')) {
          errorMessage = i18n.t('auth.invalidCredentials');
        } else if (msg.includes('email not confirmed')) {
          errorMessage = i18n.t('auth.emailNotConfirmed');
        } else if (msg.includes('user not found')) {
          errorMessage = i18n.t('auth.accountNotFound');
        } else if (status === 429 || msg.includes('too many requests') || msg.includes('rate limit')) {
          errorMessage = i18n.t('auth.tooManyLoginAttempts');
        } else if (msg.includes('network') || msg.includes('failed to fetch')) {
          errorMessage = i18n.t('auth.loginNetworkError');
        }

        // Limited demo fallback for demo user only (for development/preview purposes)
        if (email === 'demo@sme.ng' && password === 'demo123') {
          // Use minimal data - pages will fetch real data via edge function
          console.log('Demo user login - using minimal fallback data');
          const mockUser: User = {
            id: '11111111-2222-3333-4444-555555555555',
            email: 'demo@sme.ng',
            firstName: 'User',
            lastName: '',
            clientNumber: 'DEMO001',
            address: { street: '123 Victoria Island', city: 'Lagos', zipCode: '101001' },
            preferences: { emailNotifications: true, pushNotifications: true, phoneNotifications: false }
          };
          const mockToken = 'demo_token_' + Date.now();
          setUser(mockUser);
          setToken(mockToken);
          setUserRole('user');
          // No localStorage persistence for demo user - session only
          return { success: true };
        }
        return { success: false, error: errorMessage };
      }

      // onAuthStateChange will populate user and role; ensure token is stored immediately
      if (data.session?.access_token) {
        setToken(data.session.access_token);
        localStorage.setItem('sme_token', data.session.access_token);

        // Wait a bit for session to be fully established
        await new Promise(resolve => setTimeout(resolve, 100));

        // Fetch user status and organization — always, regardless of org selection
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('status, organization_id')
          .eq('id', data.user.id)
          .single();

        if (userError) {
          console.error('Failed to fetch user record:', {
            error: userError,
            message: userError.message,
            code: userError.code
          });
          await supabase.auth.signOut();
          setToken(null);
          localStorage.removeItem('sme_token');
          return {
            success: false,
            error: i18n.t('auth.accountVerifyFailed')
          };
        }

        // Block deactivated users immediately — Supabase Auth does not enforce our app status.
        // The onAuthStateChange listener ALSO checks this (and is the authoritative check that
        // controls `user` state - see its comment) since login() and that listener react to
        // this same sign-in independently; this early rejection is just a faster UI-facing
        // error rather than waiting on the listener's slower role-lookup queries.
        if (userData?.status === 'inactive') {
          console.warn('Login blocked: user account is deactivated', data.user.id);
          await supabase.auth.signOut();
          setToken(null);
          localStorage.removeItem('sme_token');
          return {
            success: false,
            error: i18n.t('auth.accountDeactivated')
          };
        }

        // CRITICAL: Validate user belongs to the selected organization
        const selectedOrgId = localStorage.getItem('organizationId');
        if (selectedOrgId && userData) {
          // If user has an organization_id, it MUST match the selected organization
          // If organization_id is NULL, user hasn't completed onboarding yet - allow login
          // EXCEPTION: Super admins (is_platform_admin) can access ANY organization
          const isSuperAdmin = data.user?.app_metadata?.is_platform_admin === true;

          if (!isSuperAdmin && userData.organization_id && userData.organization_id !== selectedOrgId) {
            console.error('Organization mismatch:', {
              userOrg: userData.organization_id,
              selectedOrg: selectedOrgId
            });
            await supabase.auth.signOut();
            setToken(null);
            localStorage.removeItem('sme_token');
            return {
              success: false,
              error: i18n.t('auth.orgNotAuthorized')
            };
          }

          if (isSuperAdmin) {
            console.log('Super admin login - bypassing organization check');
          }
        }

        // Track login event
        try {
          await supabase
            .from('login_history')
            .insert({
              user_id: data.user?.id,
              login_at: new Date().toISOString(),
              ip_address: null, // Will be populated by server if needed
              user_agent: navigator.userAgent,
              success: true
            });
        } catch (loginTrackError) {
          console.warn('Failed to track login:', loginTrackError);
          // Don't fail login if tracking fails
        }
      }
      return { success: true };
    } catch (unexpectedError: any) {
      console.error('Unexpected login error:', unexpectedError);
      return {
        success: false,
        error: i18n.t('auth.unexpectedLoginError')
      };
    } finally {
      setIsLoading(false);
      setIsValidatingOrg(false); // Allow redirects again
    }
  };

  const register = async (userData: Partial<User> & { password: string }): Promise<boolean> => {
    setIsLoading(true);
    
    // Simulation of API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const mockUser: User = {
      id: Date.now().toString(),
      email: userData.email!,
      firstName: userData.firstName!,
      lastName: userData.lastName!,
      clientNumber: Math.random().toString().substr(2, 10),
      address: {
        street: '',
        city: '',
        zipCode: ''
      },
      preferences: {
        emailNotifications: true,
        pushNotifications: true,
        phoneNotifications: false
      }
    };

    const mockToken = 'mock_jwt_token_' + Date.now();
    const expiry = new Date().getTime() + (24 * 60 * 60 * 1000);
    const role = 'user';

    setUser(mockUser);
    setToken(mockToken);
    setUserRole(role);
    
    localStorage.setItem('sme_token', mockToken);
    localStorage.setItem('sme_user', JSON.stringify(mockUser));
    localStorage.setItem('sme_user_role', role);
    localStorage.setItem('sme_token_expiry', expiry.toString());
    localStorage.setItem('onboarding_pending', 'true');
    
    setIsLoading(false);
    return true;
  };

  const updateUser = async (userData: Partial<User>): Promise<void> => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('sme_user', JSON.stringify(updatedUser));
    }
  };

  const isAdmin = (): boolean => {
    // Platform admins are always considered admins
    return userRole === 'admin' || isPlatformAdmin;
  };

  const isPlatformAdminUser = (): boolean => {
    return isPlatformAdmin;
  };

  // Alias for isPlatformAdminUser - "Super Admin" is the user-facing term
  const isSuperAdminUser = (): boolean => {
    return isPlatformAdmin;
  };

  const isOwner = (): boolean => {
    return isOwnerUser || userRole === 'owner';
  };

  const isConcierge = (): boolean => {
    return isConciergeUser || userRole === 'concierge';
  };

  const logout = () => {
    supabase.auth.signOut();
    setUser(null);
    setToken(null);
    setUserRole(null);
    setIsPlatformAdmin(false);
    setIsOwnerUser(false);
    setIsConciergeUser(false);
    setOwnedApartments([]);
    setConciergeApartments([]);
    localStorage.removeItem('sme_token');
    localStorage.removeItem('sme_user');
    localStorage.removeItem('sme_user_role');
    localStorage.removeItem('sme_token_expiry');
    localStorage.removeItem('sme_is_platform_admin');
    localStorage.removeItem('sme_is_owner');
    localStorage.removeItem('sme_is_concierge');
    localStorage.removeItem('sme_owned_apartments');
    localStorage.removeItem('sme_concierge_apartments');
    // Let React Router update based on auth state
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      userRole,
      isPlatformAdmin,
      isSuperAdmin: isPlatformAdmin, // Alias for isPlatformAdmin
      isOwnerUser,
      isConciergeUser,
      ownedApartments,
      conciergeApartments,
      effectiveOrganizationId,
      login,
      register,
      logout,
      isLoading,
      isValidatingOrg,
      updateUser,
      isAdmin,
      isPlatformAdminUser,
      isSuperAdminUser, // Alias for isPlatformAdminUser
      isOwner,
      isConcierge,
      getEffectiveOrganizationId,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
